import type { Snapshot } from '@ember-data/legacy-compat/-private';
import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationSerializer from 'lumen/serializers/application';
import type {
  SavingStatusRawResponse,
  SavingStatusResponse,
} from 'lumen/services/central-mapping/saving';
import type { Breakdown } from 'lumen/utils/central-mapping-constants';

export interface MappingEntriesRawResponseMeta {
  breakdown: Breakdown;

  pagination_meta: {
    count: number;
    page: number;

    page_count: number;

    page_size: number;
  };

  // Property `breakdown` is not used in the frontend for now, but it's included in the response meta.
  status: SavingStatusRawResponse;
}

export interface MappingEntriesResponseMeta {
  // Status is only sent for market mapping, not hotel mapping.
  paginationMeta?: {
    endIndex: number;
    page: number;
    pageCount: number;
    pageSize: number;
    startIndex: number;
    totalNoOfItems: number;
  };
  status?: SavingStatusResponse;
}

export const MAPPING_ENTRY_ATTRS = {
  userCategory: 'user_category_id',
  autoCategory: 'auto_category_id',
  subscription: 'subscription_id',
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class CentralMappingMappingEntrySerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = MAPPING_ENTRY_ATTRS;

  extractMeta(
    store: Store,
    modelClass: ModelSchema,
    payload: Record<string, unknown>,
  ): MappingEntriesResponseMeta {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    let meta = super.extractMeta(store, modelClass, payload);

    function calculatePaginationInfo(paginationMeta: {
      count: number;
      page: number;

      page_count: number;

      page_size: number;
    }): NonNullable<MappingEntriesResponseMeta['paginationMeta']> {
      const {
        count: totalNoOfItems,
        page_size: pageSize,
        page: currentPage,
      } = paginationMeta;

      const startIndex = (currentPage - 1) * pageSize + 1;
      const endIndex = Math.min(currentPage * pageSize, totalNoOfItems);

      return {
        startIndex,
        endIndex,
        totalNoOfItems,
        pageSize,
        page: currentPage,
        pageCount: paginationMeta.page_count,
      };
    }

    if (!meta) {
      meta = {};
    }

    const paginationMeta = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      count: meta.count,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      page_size: meta.page_size,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      page_count: meta.page_count,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      page: meta.page,
    };

    const camelizedMeta: MappingEntriesResponseMeta = {
      paginationMeta: calculatePaginationInfo(paginationMeta),
    };

    // `status` is optional, so check if it exists before normalizing it.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (meta.status) {
      let camelizedSavingStatusResponse: SavingStatusResponse = {
        inProgress: false,
        totalSubscriptionsProcessing: null,
        subscriptionsProcessed: null,
      };

      // TS is quite annoying with the union type, so I solved it with an `if` check on `in_progress` to properly type the status.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (meta.status.in_progress) {
        camelizedSavingStatusResponse = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          inProgress: meta.status.in_progress,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          totalSubscriptionsProcessing:
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            meta.status.total_subscriptions_processing,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          subscriptionsProcessed: meta.status.subscriptions_processed,
        };

        camelizedMeta.status = camelizedSavingStatusResponse;
      }
    }
    return camelizedMeta;
  }

  modelNameFromPayloadKey(key: string): string {
    if (key === 'data') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return super.modelNameFromPayloadKey('central-mapping/mapping-entry');
    }

    return key;
  }

  serialize(snapshot: Snapshot, options: object): Record<string, unknown> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const payload = super.serialize(snapshot, options);

    // Don't include the `auto_category_id`. They are not meant to be changed in the frontend, and backend ignores it anyway.
    if ('auto_category_id' in payload) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      delete payload.auto_category_id;
    }

    // backend expects `subscription_id` and `id` to be an integer
    if ('subscription_id' in payload) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload.subscription_id = Number(payload.subscription_id);
    }
    if ('id' in payload) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload.id = Number(payload.id);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return payload;
  }
}
